/**
 * ODS — OpenDocument spreadsheet (§8 Phase 3, §15).
 *
 * The model is an {@link XlsxModel}, so the grid, the formula bar, cell
 * selection and the §15 editing rules all apply to an ODS unchanged. That
 * matters most for the rule §15 exists to enforce: **this application has no
 * calculation engine.** A formula cell shows its last saved result, the formula
 * bar shows the formula that produced it, and typing over that formula replaces
 * it with a literal — said before the edit lands, not after. Reusing the model
 * means an ODS cannot accidentally get a different, softer version of that
 * promise.
 *
 * `XlsxModel.edits` is keyed by part path, which in ODF would collide because
 * every sheet lives in the same `content.xml`. Each sheet's key carries its
 * index as a fragment, so the shared {@link setCell} works without a special
 * case and the export path knows which table to write into.
 */
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type {
  ExportResult, FileSource, FormatHandler, HandlerContext,
  HandlerWarning, OpenedDocument, Preview, ValidationReport,
} from '../contract.js';
import { HandlerError, throwIfAborted } from '../contract.js';
import { repackPackage } from '../ooxml/package.js';
import type { XlsxModel, XlsxSheet } from '../xlsx/handler.js';
import { CONTENT_PART, ODF_MIME, checkMimetypeEntry, openOdf } from './package.js';
import { applyOdfCellEdits, parseOdfSheets } from './sheet-xml.js';

const PREVIEW_CELLS = 2_000;

/* ------------------------------------------------------------------ *
 * handler
 * ------------------------------------------------------------------ */

export const odsHandler: FormatHandler<XlsxModel> = {
  id: 'ods',
  formatIds: ['ods'],

  async probe(source: FileSource, ctx: HandlerContext): Promise<Preview> {
    throwIfAborted(ctx.signal);
    const doc = openOdf(source.bytes);
    // §11: enough of the first sheet to paint a screen, not the workbook.
    const parsed = parseOdfSheets(doc.contentXml, { maxCells: PREVIEW_CELLS });
    const first = parsed.sheets[0];

    return {
      kind: 'sheets',
      unitCount: parsed.sheets.length,
      unitKey: 'viewer.sheet',
      firstContent: {
        name: first?.name ?? '',
        cells: first?.cells.slice(0, PREVIEW_CELLS) ?? [],
        rowCount: first?.rowCount ?? 0,
        colCount: first?.colCount ?? 0,
        more: parsed.truncated,
      },
      warnings: [],
    };
  },

  async open(source: FileSource, ctx: HandlerContext): Promise<OpenedDocument<XlsxModel>> {
    throwIfAborted(ctx.signal);
    const doc = openOdf(source.bytes);
    throwIfAborted(ctx.signal);

    const parsed = parseOdfSheets(doc.contentXml, { maxCells: ctx.limits.maxSpreadsheetCells });
    if (parsed.sheets.length === 0) {
      throw new HandlerError('error.damagedOrIncomplete', 'the workbook declares no sheets');
    }

    const hasMacros = doc.pkg.directory.entries.some((e) =>
      e.name.startsWith('Basic/') || e.name.startsWith('Scripts/'));
    // A linked source is a network read this application will never perform,
    // so its presence changes what the shown numbers mean (§29).
    const hasExternalLinks = doc.contentXml.includes('<table:table-source')
      || /xlink:href\s*=\s*"[a-z][a-z0-9+.-]*:\/\//i.test(doc.contentXml);

    const spec = FORMATS_BY_ID.get(source.formatId);
    const warnings: HandlerWarning[] = [];

    if (doc.mimetype && doc.mimetype !== ODF_MIME['ods']) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'caution',
        detail: `the package declares ${doc.mimetype}, which is not an OpenDocument spreadsheet`,
      });
    }
    if (parsed.hasFormulas) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: 'Formulas are shown with their last saved result. This application does not recalculate, and typing over a formula replaces it with the value typed (§15).',
      });
    }
    if (parsed.truncated) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'degraded',
        detail: `the workbook exceeds the ${ctx.limits.maxSpreadsheetCells} cell budget for this device; the remainder is not shown`,
      });
    }
    if (hasMacros) {
      warnings.push({
        messageKey: 'security.macrosPresent',
        severity: 'caution',
        detail: 'This workbook carries Basic macros. They are never executed and are preserved unchanged on save (§30).',
      });
    }
    if (hasExternalLinks) {
      warnings.push({
        messageKey: 'security.externalContent',
        severity: 'caution',
        detail: 'This workbook links to external data. Nothing is fetched, so the values shown are the ones last saved into the file (§29).',
      });
    }
    if (parsed.protectedSheets.length > 0) {
      warnings.push({
        messageKey: 'status.limitedSupport',
        severity: 'info',
        detail: `Sheet protection is recorded on ${parsed.protectedSheets.join(', ')}. The flag is preserved but not enforced by this viewer.`,
      });
    }
    if (source.sizeBytes > 20 * 1024 * 1024) {
      warnings.push({
        messageKey: 'status.loadingRemaining',
        severity: 'info',
        detail: `large workbook (${formatFileSize('en', source.sizeBytes)})`,
      });
    }

    return {
      formatId: source.formatId,
      model: {
        sheets: parsed.sheets,
        activeSheet: 0,
        edits: new Map(),
        hasMacros,
        hasFormulas: parsed.hasFormulas,
        hasExternalLinks,
      },
      readOnly: ctx.assessment.forceReadOnly,
      editLevel: spec?.edit ?? 'basic-edit',
      lossOnSave: spec?.lossOnSave ?? [],
      warnings,
    };
  },

  async exportBytes(
    model: XlsxModel, source: FileSource, ctx: HandlerContext,
  ): Promise<ExportResult> {
    throwIfAborted(ctx.signal);
    const doc = openOdf(source.bytes);
    const replacements = new Map<string, Uint8Array>();
    const dropped: string[] = [];

    if (model.edits.size > 0) {
      let xml = doc.contentXml;
      for (const [key, cells] of model.edits) {
        if (cells.size === 0) continue;
        const sheetIndex = sheetIndexOf(key);
        if (sheetIndex === null) {
          throw new HandlerError('error.unableToRead', `unrecognised sheet key: ${key}`);
        }
        const result = applyOdfCellEdits(
          xml, sheetIndex,
          [...cells].map(([ref, value]) => ({ ref, value })),
        );
        if (result.failed.length > 0) {
          throw new HandlerError(
            'error.unableToRead', `could not write cells: ${result.failed.join(', ')}`,
          );
        }
        for (const ref of result.formulasReplaced) {
          const name = model.sheets[sheetIndex]?.name ?? String(sheetIndex);
          dropped.push(`${name}!${ref}: 수식이 입력값으로 대체됨`);
        }
        xml = result.xml;
      }
      replacements.set(CONTENT_PART, new TextEncoder().encode(xml));
    }

    const repacked = repackPackage(doc.pkg, replacements);
    return {
      bytes: repacked.bytes,
      suggestedName: source.filename,
      formatId: source.formatId,
      dropped,
    };
  },

  async validateExport(
    bytes: Uint8Array, model: XlsxModel, ctx: HandlerContext,
  ): Promise<ValidationReport> {
    throwIfAborted(ctx.signal);
    const issues: string[] = [];
    let reopened = false;
    let contentPreserved = false;

    try {
      const doc = openOdf(bytes);
      const parsed = parseOdfSheets(doc.contentXml, { maxCells: ctx.limits.maxSpreadsheetCells });
      reopened = true;
      issues.push(...checkMimetypeEntry(doc.pkg, ODF_MIME['ods']!));

      if (parsed.sheets.length !== model.sheets.length) {
        issues.push(`sheet count changed: ${model.sheets.length} → ${parsed.sheets.length}`);
      }

      for (const [index, expected] of model.sheets.entries()) {
        const actual = parsed.sheets[index];
        if (!actual) { issues.push(`sheet ${expected.name} disappeared`); continue; }
        if (actual.name !== expected.name) {
          issues.push(`sheet ${index} renamed: ${expected.name} → ${actual.name}`);
        }
        issues.push(...compareCells(expected, actual));
      }

      contentPreserved = issues.length === 0;
    } catch (err) {
      issues.push(`could not reopen the exported workbook: ${(err as Error).message}`);
    }

    return { ok: reopened && contentPreserved, reopened, contentPreserved, issues };
  },
};

/**
 * Compare a saved sheet against the model that produced it.
 *
 * Only cells the model holds are checked, and only their text. A writer is
 * entitled to normalise how it stores a value; what must survive is what the
 * user sees. Comparing the raw XML would fail on differences that are not
 * losses, which would make the check useless rather than strict.
 */
function compareCells(expected: XlsxSheet, actual: XlsxSheet): string[] {
  const issues: string[] = [];
  const byRef = new Map(actual.cells.map((c) => [c.ref, c]));

  for (const cell of expected.cells) {
    if (cell.text === '' && cell.formula === null) continue;
    const found = byRef.get(cell.ref);
    if (!found) {
      issues.push(`${expected.name}!${cell.ref} disappeared (was ${JSON.stringify(cell.text)})`);
      continue;
    }
    if (found.text !== cell.text) {
      issues.push(`${expected.name}!${cell.ref}: ${JSON.stringify(cell.text)} → ${JSON.stringify(found.text)}`);
    }
    // A formula that survived must have survived intact; one that was replaced
    // by a literal is reported as dropped at export time, not as an error here.
    if (cell.formula !== null && found.formula !== null && found.formula !== cell.formula) {
      issues.push(`${expected.name}!${cell.ref} formula changed: ${cell.formula} → ${found.formula}`);
    }
  }
  return issues;
}

/** `content.xml#2` → 2. Null for anything else. */
export function sheetIndexOf(partKey: string): number | null {
  const hash = partKey.lastIndexOf('#');
  if (hash < 0) return null;
  const index = Number.parseInt(partKey.slice(hash + 1), 10);
  return Number.isInteger(index) && index >= 0 ? index : null;
}
